<template>
    <div class="trailer-wrapper">
        <div class="trailer-shield"> </div>
        <div ref="frame" class="yt-player"> </div>
        <div v-if="showCta" class="watch-cta-wrap">

            <div class="watch-cta" :key="ctaKey" :class="{delay: delay}" @animationend.self="showCta = false">
                <span class="watch-cta__icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                        <path d="M8 5v14l11-7z" fill="currentColor" />
                    </svg>
                </span>
                <span class="watch-cta__label" v-if="!delay">Watch now</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'YtEmbededTrailer',

    props: {
        embedKey: String,
        delay: {
            type: Boolean,
            default: () => {return false}
        },
        muted: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            ready: false,
            isMuted: this.muted,
            showCta: false,
            ctaKey: 0,
        }
    },

    computed: {
    },

    watch: {
        muted(val) {
            this.setMuted(val)
        }
    },

    mounted() {
        this.createTrailerIframe()
        if (this.delay) this.addFocusListeners()
    },

    // Vue 3
    beforeUnmount() {
        this.teardown()
    },

    // Vue 2
    beforeDestroy() {
        this.teardown()
    },

    methods: {
        teardown() {
            this.removeFocusListeners()
            window.removeEventListener('message', this._onMessage)
            this._onMessage = null
            clearTimeout(this._blurTimer)
            this._iframe = null
        },

        addFocusListeners() {
            this._isAway = false

            this._onVisibilityChange = () => {
                document.hidden ? this.handleLeave() : this.handleReturn()
            }

            document.addEventListener('visibilitychange', this._onVisibilityChange)
        },

        removeFocusListeners() {
            document.removeEventListener('visibilitychange', this._onVisibilityChange)
            this._onVisibilityChange = null
        },

        handleLeave() {
            if (this._isAway) return
            this._isAway = true
            this.onLeave()
        },

        handleReturn() {
            if (!this._isAway) return
            this._isAway = false
            this.onReturn()
        },

        onLeave() {
            this.hideIframe()
        },

        onReturn() {
            this.revealIframe()
        },

        hideIframe() {
            if(!this.ready){return}
            if (!this._iframe) return
            this._iframe.style.transition = 'none'
            this._iframe.style.opacity = '0'
        },

        revealIframe() {
            if(!this.ready){return}
            if (!this._iframe) return
            this.ctaKey++
            this.showCta = true
            this._iframe.style.transition = 'opacity 0.8s ease'
            this._iframe.style.opacity = '1'
        },

        postCommand(func, args = '') {
            const win = this._iframe && this._iframe.contentWindow
            if (!win) return
            win.postMessage(JSON.stringify({ event: 'command', func, args }), 'https://www.youtube.com')
        },

        applyMute() {
            this.postCommand(this.isMuted ? 'mute' : 'unMute')
        },

        setMuted(val) {
            this.isMuted = val
            this.applyMute()
        },

        toggleMute() {
            this.setMuted(!this.isMuted)
            return this.isMuted
        },

        loopVideo() {
            this.showCta = true
            const now = Date.now()
            if (this._lastLoop && now - this._lastLoop < 1000) return
            this._lastLoop = now
            this.postCommand('seekTo', [15, true])
            this.postCommand('playVideo')
        },

        //figure out hack for overlay
        createTrailerIframe() {
            const el = this.$refs.frame
            if (!el) return
            const preparams = [
                'autoplay=1',
                'mute=1',
                'enablejsapi=1',
            ]

            if(this.delay){
                preparams.push('start=15')
            }

           const params =  preparams.join('&')

            const iframe = document.createElement('iframe')
            iframe.src = 'https://www.youtube.com/embed/' + this.embedKey + '?' + params
            iframe.setAttribute('frameborder', '0')
            iframe.setAttribute('allow', 'autoplay; encrypted-media')
            if(this.delay){
                iframe.style.width = '250%'
                iframe.style.height = '250%'
            }else{

                iframe.style.width = '185%'
                iframe.style.height = '185%'
            }
            iframe.style.position = 'absolute'
            iframe.style.top = '50%'
            iframe.style.left = '50%'
            iframe.style.transform = 'translate(-50%, -50%)'
            iframe.style.border = 'none'
            iframe.style.opacity = '0'
            iframe.style.transition = 'opacity 0.8s ease'
            iframe.style.pointerEvents = 'none'
            el.appendChild(iframe)
            this._iframe = iframe

            // Only react to messages from *this* instance's iframe so multiple trailers don't interfere
            this._onMessage = (e) => {
                if (!this._iframe || e.source !== this._iframe.contentWindow) return
                let data = e.data
                if (typeof data === 'string') {
                    try { data = JSON.parse(data) } catch (err) { return }
                }
                if (!data) return
                if (data.event === 'onReady') this.applyMute()
                if (data.event === 'onStateChange' && data.info === 0) this.loopVideo() // 0 = ended
                // fallback: the player also reports state via infoDelivery
                if (data.event === 'infoDelivery' && data.info && data.info.playerState === 0) this.loopVideo()
            }
            window.addEventListener('message', this._onMessage)

            iframe.addEventListener('load', () => {
                this.ready = true
                // opt in to player events (needed to receive onReady / onStateChange)
                iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), 'https://www.youtube.com')
                // onStateChange is only sent if explicitly subscribed to
                this.postCommand('addEventListener', ['onStateChange'])
                this.applyMute()
                this.revealIframe()
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.trailer-wrapper{
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    overflow: hidden;
}
iframe {
    z-index: 1;
}
.trailer-shield {
    position: absolute;
    inset: 0;
    overflow: hidden;
    z-index: 10;
    pointer-events: auto;
    width: 100%;
    height: 100%;
    z-index: 2;
}

.yt-player, iframe {
    position: absolute;
    z-index: 1;
    inset: 0;
    overflow: hidden;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.watch-cta-wrap {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.watch-cta {
    display: flex;
    align-items: center;
    height: 57.5px;
    width: 57.5px;
    border-radius: 29px;
    overflow: hidden;
    color: #fff;
    background: rgba(18, 18, 18, 1);
    box-sizing: border-box;
    opacity: 0;
    animation: watch-cta 5s ease-in-out forwards;
    &.delay{
        background: rgba(18, 18, 18, 0.2);
        backdrop-filter: blur(25px) brightness(1.3);
        opacity: 0.01;
        animation: fade-cta 5s ease-in-out forwards;
        .watch-cta__icon {
            opacity: 0.6;
        }
    }
}

.watch-cta__icon {
    flex: 0 0 57.5px;
    height: 53px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        margin-left: 1px; // optical centering for the triangle
    }
}

.watch-cta__label {
    padding-right: 22px;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0;
    animation: watch-cta-label 5s ease-in-out forwards;
}

@keyframes watch-cta {
    0%   { opacity: 1; width: 57.5px;  transform: scale(0.85); }
    5%   { opacity: 1; width: 57.5px;  transform: scale(1); }
    10%  { opacity: 1; width: 57.5px;  transform: scale(1); }
    16%  { opacity: 1; width: 161px; transform: scale(1); } // quick grow (~0.3s)
    84%  { opacity: 1; width: 161px; transform: scale(1); } // long hold (~3.4s)
    90%  { opacity: 1; width: 57.5px;  transform: scale(1); } // quick shrink (~0.3s)
    96%  { opacity: 1; width: 57.5px;  transform: scale(1); }
    100% { opacity: 0; width: 57.5px;  transform: scale(0.85); }
}

@keyframes fade-cta {
    0%   { opacity: 1; width: 57.5px;  transform: scale(0.85); }
    5%   { opacity: 1; width: 57.5px;  transform: scale(1); }
    10%  { opacity: 1; width: 57.5px;  transform: scale(1); }
    90%  { opacity: 1; width: 57.5px;  transform: scale(1); } // quick shrink (~0.3s)
    96%  { opacity: 1; width: 57.5px;  transform: scale(1); }
    100% { opacity: 0; width: 57.5px;  transform: scale(0.85); }
}



@keyframes watch-cta-label {
    0%, 12% { opacity: 0; }
    18%     { opacity: 1; }
    78%     { opacity: 1; }
    84%, 100% { opacity: 0; }
}
</style>
